/**
 * Service consolidé pour la gestion des cartes
 * Combine les fonctionnalités de cardService.js et CardManagementService.js
 * Refactorisé pour utiliser axios avec Bearer token automatique
 */

import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BASEURL_CLIENT_BACKEND;

// Configuration des headers avec Bearer token
const headers = {
    'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem('userDetails'))?.token,
    'Accept-Language': localStorage.getItem('currentLanguage')
};

// Création de l'instance axios
const CardServiceRequest = axios.create({
    baseURL: API_BASE_URL,
    headers: headers
});

class CardManagementService {

    // ===== RECHERCHE ET RÉCUPÉRATION DE CARTES =====

    // Recherche générale avec pagination
    async searchCards(params = {}) {
        const urlParams = {
            page: params.page || 1,
            pageSize: params.pageSize || 50,
            searchTerm: params.searchTerm || '',
            sortBy: params.sortBy || 'ProductionDate',
            sortDirection: params.sortDirection || 'desc',
            ...params
        };

        const response = await CardServiceRequest.get('/Card/list', { params: urlParams });
        return response.data;
    }

    // Recherche par ID de carte
    async getCardDetails(cardId) {
        if (!cardId || isNaN(cardId)) {
            throw new Error('ID de carte invalide');
        }

        try {
            const response = await CardServiceRequest.get(`/Card/${cardId}`);
            return response.data;
        } catch (error) {
            if (error.response?.status === 404) {
                throw new Error(`Aucune carte trouvée avec l'ID ${cardId}`);
            }
            throw new Error(`Erreur lors de la recherche: ${error.response?.status}`);
        }
    }

    // Recherche par numéro de carte
    async getCardByNumber(cardNumber) {
        if (!cardNumber || !cardNumber.trim()) {
            throw new Error('Numéro de carte requis');
        }

        try {
            const response = await CardServiceRequest.get(`/Card/number/${cardNumber}`);
            return response.data;
        } catch (error) {
            if (error.response?.status === 404) {
                throw new Error(`Aucune carte trouvée avec le numéro ${cardNumber}`);
            }
            throw new Error(`Erreur lors de la recherche: ${error.response?.status}`);
        }
    }

    // Recherche par ID bénéficiaire
    async getCardsByBeneficiary(beneficiaryId) {
        if (!beneficiaryId) {
            throw new Error('ID bénéficiaire requis');
        }

        try {
            // Méthode principale via l'endpoint dédié
            const response = await CardServiceRequest.get(`/Card/beneficiary/${beneficiaryId}`);
            return response.data;
        } catch (error) {
            // Fallback si l'endpoint principal échoue
            if (error.response?.status === 404) {
                return [];
            }

            // Fallback via l'API de liste avec filtrage
            try {
                const fallbackParams = {
                    page: 1,
                    pageSize: 50,
                    beneficiaryId: beneficiaryId,
                    sortBy: 'ProductionDate',
                    sortDirection: 'desc'
                };

                const fallbackResponse = await this.searchCards(fallbackParams);

                if (fallbackResponse.data) {
                    return fallbackResponse.data.filter(card =>
                        card.beneficiaryId === parseInt(beneficiaryId)
                    );
                }

                return [];
            } catch (fallbackError) {
                return [];
            }
        }
    }

    // ===== GESTION DES STATUTS =====

    // Récupérer les cartes par statut avec pagination
    async getCardsByStatus(status, page = 1, pageSize = 50) {
        const params = {
            pageNumber: page,
            pageSize: pageSize
        };

        const response = await CardServiceRequest.get(`/Card/status/${status}`, { params });
        const cards = response.data;
        return Array.isArray(cards) ? cards : [];
    }

    // Obtenir les compteurs de cartes par statut
    async getCardStatistics() {
        const statuses = [
            { api: 'DataValidation', key: 'dataValidation' },
            { api: 'PhotoProcessing', key: 'photoProcessing' },
            { api: 'QualityControl', key: 'qualityControl' },
            { api: 'Printing', key: 'printing' },
            { api: 'Shipped', key: 'shipped' },
            { api: 'Delivered', key: 'delivered' },
            { api: 'Blocked', key: 'blocked' },
            { api: 'Cancelled', key: 'cancelled' }
        ];

        const counts = {};

        for (const status of statuses) {
            try {
                const cards = await this.getCardsByStatus(status.api, 1, 1000);
                counts[status.key] = cards.length;
            } catch (error) {
                console.warn(`Erreur pour le statut ${status.api}:`, error);
                counts[status.key] = 0;
            }
        }

        return counts;
    }

    // Mettre à jour le statut d'une carte
    async updateCardStatus(cardId, statusData) {
        const response = await CardServiceRequest.put(`/Card/${cardId}/status`, {
            status: statusData.status,
            reason: statusData.reason,
            updatedBy: statusData.updatedBy || 'admin@company.com'
        });
        return response.data;
    }

    // ===== BLOCAGE ET DÉBLOCAGE =====

    // Bloquer une carte
    async blockCard(cardId, blockData) {
        const response = await CardServiceRequest.post(`/Card/${cardId}/block`, {
            reason: blockData.reason,
            isTemporary: blockData.isTemporary || false,
            expiryDate: blockData.expiryDate,
            comment: blockData.comment,
            blockedBy: blockData.blockedBy || 'admin@company.com'
        });
        return response.data;
    }

    // Débloquer une carte
    async unblockCard(cardId, unblockData) {
        const response = await CardServiceRequest.post(`/Card/${cardId}/unblock`, {
            reason: unblockData.reason,
            comment: unblockData.comment,
            unblockedBy: unblockData.unblockedBy || 'admin@company.com'
        });
        return response.data;
    }

    // Activer une carte
    async activateCard(cardId, activationData) {
        const response = await CardServiceRequest.post(`/Card/${cardId}/activate`, {
            activationCode: activationData.activationCode || `ACT-${Date.now()}`,
            activatedBy: activationData.activatedBy || 'admin@company.com'
        });
        return response.data;
    }

    // Blocage en masse
    async bulkBlockCards(cardIds, blockData) {
        const response = await CardServiceRequest.post('/Card/bulk-block', {
            cardIds,
            reason: blockData.reason,
            isTemporary: blockData.isTemporary || false,
            expiryDate: blockData.expiryDate,
            comment: blockData.comment,
            blockedBy: blockData.blockedBy || 'admin@company.com'
        });
        return response.data;
    }

    // Déblocage en masse
    async bulkUnblockCards(cardIds, unblockData) {
        const response = await CardServiceRequest.post('/Card/bulk-unblock', {
            cardIds,
            reason: unblockData.reason,
            comment: unblockData.comment,
            unblockedBy: unblockData.unblockedBy || 'admin@company.com'
        });
        return response.data;
    }

    // ===== HISTORIQUE =====

    async getCardStatusHistory(cardId) {
        const response = await CardServiceRequest.get(`/Card/${cardId}/status-history`);
        return response.data;
    }

    async getBlockHistory(cardId) {
        const response = await CardServiceRequest.get(`/Card/${cardId}/block-history`);
        return response.data;
    }

    // ===== PRODUCTION =====

    // Initier la production d'une carte
    async initiateCardProduction(productionData) {
        const response = await CardServiceRequest.post('/Card/initiate-production', {
            beneficiaryId: parseInt(productionData.beneficiaryId),
            countryCode: productionData.countryCode,
            templateId: parseInt(productionData.templateId),
            priority: productionData.priority,
            requestedBy: productionData.requestedBy,
            photoPath: productionData.photoPath
        });
        return response.data;
    }

    // Vérifier le statut de production d'une carte
    async getProductionStatus(cardId) {
        const response = await CardServiceRequest.get(`/Card/production-status/${cardId}`);
        return response.data;
    }

    // ===== APERÇU ET IMPRESSION =====

    // Générer un aperçu de carte
    async generateCardPreview(cardId, options = {}) {
        const response = await CardServiceRequest.get(`/Cards/${cardId}/preview`, {
            params: options,
            responseType: 'blob'
        });
        return response.data;
    }

    // ===== EXPORT =====

    async exportCardsData(filters = {}, format = 'excel') {
        const params = {
            format: format,
            ...filters
        };

        const response = await CardServiceRequest.get('/Cards/export', {
            params,
            responseType: 'blob'
        });
        return response.data;
    }

    // ===== STATISTIQUES DE BLOCAGE =====

    async getBlockingStatistics(filters = {}) {
        const response = await CardServiceRequest.get('/Cards/blocking-statistics', {
            params: filters
        });
        return response.data;
    }

    // ===== MÉTHODES UTILITAIRES =====

    // Créer une URL blob pour aperçu
    createBlobUrl(blob) {
        return URL.createObjectURL(blob);
    }

    // Télécharger un fichier
    downloadFile(blob, filename) {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }

    // Validation helpers
    validateCardNumber(cardNumber) {
        return /^[0-9]{16}$/.test(cardNumber);
    }

    validateBeneficiaryId(beneficiaryId) {
        return /^[0-9]+$/.test(beneficiaryId);
    }

    // Formater le statut pour l'affichage
    formatStatus(status) {
        const statusMap = {
            'DataValidation': 'Validation données',
            'PhotoProcessing': 'Traitement photo',
            'TemplateApplication': 'Application template',
            'QualityControl': 'Contrôle qualité',
            'Printing': 'En impression',
            'Packaging': 'Emballage',
            'Shipped': 'Expédiée',
            'Delivered': 'Livrée',
            'Blocked': 'Bloquée',
            'Cancelled': 'Annulée'
        };
        return statusMap[status] || status;
    }

    // Obtenir la configuration du badge de statut
    getStatusBadgeConfig(status) {
        const statusConfig = {
            'DataValidation': { variant: 'info', text: 'Validation données' },
            'PhotoProcessing': { variant: 'info', text: 'Traitement photo' },
            'TemplateApplication': { variant: 'info', text: 'Application template' },
            'QualityControl': { variant: 'warning', text: 'Contrôle qualité' },
            'Printing': { variant: 'primary', text: 'Impression' },
            'Packaging': { variant: 'primary', text: 'Emballage' },
            'Shipped': { variant: 'success', text: 'Expédiée' },
            'Delivered': { variant: 'success', text: 'Livrée' },
            'Blocked': { variant: 'danger', text: 'Bloquée' },
            'Cancelled': { variant: 'danger', text: 'Annulée' }
        };

        return statusConfig[status] || { variant: 'secondary', text: status };
    }

    // ===== GESTION DES TEMPLATES =====

    // Récupération des templates de cartes
    async getCardTemplates(filters = {}) {
        try {
            const params = {};

            if (filters.isActive !== undefined) {
                params.isActive = filters.isActive;
            }
            if (filters.countryCode) {
                params.countryCode = filters.countryCode;
            }
            if (filters.category) {
                params.category = filters.category;
            }
            if (filters.cardType) {
                params.cardType = filters.cardType;
            }

            const response = await CardServiceRequest.get('/api/card/Templates', { params });
            const data = response.data;
            return Array.isArray(data) ? data : [];
        } catch (error) {
            console.error('Erreur dans getCardTemplates:', error);
            throw error;
        }
    }

    // Récupération d'un template par ID
    async getCardTemplate(templateId) {
        if (!templateId) {
            throw new Error('ID du template requis');
        }

        try {
            const response = await CardServiceRequest.get(`/api/card/Templates/${templateId}`);
            return response.data;
        } catch (error) {
            if (error.response?.status === 404) {
                throw new Error(`Template non trouvé avec l'ID ${templateId}`);
            }
            console.error('Erreur dans getCardTemplate:', error);
            throw error;
        }
    }

    // Création d'un nouveau template
    async createCardTemplate(templateData) {
        try {
            const response = await CardServiceRequest.post('/api/card/Templates', templateData);
            return response.data;
        } catch (error) {
            console.error('Erreur dans createCardTemplate:', error);
            throw new Error(`Erreur lors de la création du template: ${error.response?.status}`);
        }
    }

    // Mise à jour d'un template existant
    async updateCardTemplate(templateId, templateData) {
        if (!templateId) {
            throw new Error('ID du template requis');
        }

        try {
            const response = await CardServiceRequest.put(`/api/card/Templates/${templateId}`, templateData);
            return response.data;
        } catch (error) {
            if (error.response?.status === 404) {
                throw new Error(`Template non trouvé avec l'ID ${templateId}`);
            }
            console.error('Erreur dans updateCardTemplate:', error);
            throw new Error(`Erreur lors de la mise à jour du template: ${error.response?.status}`);
        }
    }

    // Suppression d'un template
    async deleteCardTemplate(templateId) {
        if (!templateId) {
            throw new Error('ID du template requis');
        }

        try {
            await CardServiceRequest.delete(`/api/card/Templates/${templateId}`);
            return true;
        } catch (error) {
            if (error.response?.status === 404) {
                throw new Error(`Template non trouvé avec l'ID ${templateId}`);
            }
            console.error('Erreur dans deleteCardTemplate:', error);
            throw new Error(`Erreur lors de la suppression du template: ${error.response?.status}`);
        }
    }

    // Prévisualisation d'un template avec données
    async previewCardTemplate(previewData) {
        try {
            const response = await CardServiceRequest.post('/api/card/Templates/preview', previewData);
            return response.data;
        } catch (error) {
            console.error('Erreur dans previewCardTemplate:', error);
            throw new Error(`Erreur lors de la génération de la prévisualisation: ${error.response?.status}`);
        }
    }

    // ===== PRODUCTION PAR LOT =====

    // Production par lot de cartes
    async batchPrintCards(batchData) {
        try {
            const response = await CardServiceRequest.post('/api/card/Templates/batch-print', batchData);
            return response.data;
        } catch (error) {
            console.error('Erreur dans batchPrintCards:', error);
            throw new Error(`Erreur lors de l'impression par lot: ${error.response?.status}`);
        }
    }

    // Production par lot en PDF
    async batchPrintCardsPdf(batchData) {
        try {
            const response = await CardServiceRequest.post('/api/card/Templates/batch-print-pdf', batchData);
            return response.data;
        } catch (error) {
            console.error('Erreur dans batchPrintCardsPdf:', error);
            throw new Error(`Erreur lors de l'impression PDF par lot: ${error.response?.status}`);
        }
    }

    // Suivi du statut d'un job d'impression
    async getPrintJobStatus(jobId) {
        if (!jobId) {
            throw new Error('ID du job requis');
        }

        try {
            const response = await CardServiceRequest.get(`/api/card/Templates/print-jobs/${jobId}/status`);
            return response.data;
        } catch (error) {
            if (error.response?.status === 404) {
                throw new Error(`Job non trouvé avec l'ID ${jobId}`);
            }
            console.error('Erreur dans getPrintJobStatus:', error);
            throw new Error(`Erreur lors de la vérification du statut: ${error.response?.status}`);
        }
    }

    // ===== GESTION DES LOTS DE CARTES =====

    // Récupération des lots de cartes
    async getCardBatches(filters = {}) {
        try {
            const params = {};

            if (filters.countryCode) {
                params.countryCode = filters.countryCode;
            }
            if (filters.pageNumber) {
                params.pageNumber = filters.pageNumber;
            }
            if (filters.pageSize) {
                params.pageSize = filters.pageSize;
            }

            const response = await CardServiceRequest.get('/Card/batches', { params });
            const data = response.data;
            return Array.isArray(data) ? data : [];
        } catch (error) {
            console.error('Erreur dans getCardBatches:', error);
            throw error;
        }
    }

    // Création d'un nouveau lot de cartes
    async createCardBatch(batchData) {
        try {
            const response = await CardServiceRequest.post('/Card/batches', batchData);
            return response.data;
        } catch (error) {
            console.error('Erreur dans createCardBatch:', error);
            throw new Error(`Erreur lors de la création du lot: ${error.response?.status}`);
        }
    }
}

export default new CardManagementService();
